import { PayTerms } from "../../components/pages/Cases/CaseEngagementDoc/EngagementEnums";
import { useAccountState } from "../../redux/reducers/accountReducer";
import { useGuid } from "../../utilities/useGuid";
import { FileUploadInfo } from "../services/FileUploadManager";
import { AccountSettings, FeatureSet, MetricDisplay, UserAccount } from "./authTypes";
import { Address, Case, CaseDocument, CaseEvent, Contact, DocumentReference, Individual, Lifecycle, Note, Party, PartyWithContacts, Phone, Task, TaskStatus, TimeTrackingEntry, User, CaseExpense, CaseEngagementDoc, EngagementTemplate, FieldEntry, UserRef, CaseTemplate, CaseEventTemplate, CaseStatus, CaseStatusChange, CaseMilestone, CaseMilestoneTemplate, TaskTemplate, Invoice, CaseSystemStatus } from "./commonTypes"
import { PermissionType, UserPermissions } from "./permissionTypes";

export const useInitialValues = () => {

    //Hooks
    const { primaryAccount } = useAccountState();
    const { account } = primaryAccount as UserAccount ?? {};
    const guid = useGuid();

    const lifecycle = (): Lifecycle => {
        return {
            createdOn: undefined,
            createdBy: '',
            modifiedOn: undefined,
            modifiedBy: '',
            deletedOn: undefined,
            deletedBy: '',
        }
    }

    const address = (): Address => {
        return {
            address1: '',
            address2: '',
            city: '',
            state: '',
            country: '',
            zip: '',
            lifecycle: lifecycle()
        }
    }

    const phone = (): Phone => {
        return {
            number: '',
            type: undefined,
            primary: false,
            lifecycle: lifecycle()
        }
    }

    const individual = (): Individual => {
        return {
            accountId: account.accountId || '',
            name: '',
            title: '',
            address: address(),
            email: '',
            lifecycle: lifecycle(),
        }
    }

    const user = (userId?: string): User => {
        return {
            accountId: account.accountId || '',
            userId: userId || '',
            name: '',
            username: '',
            address: address(),
            phone: phone(),
            email: '',
            acceptedTOS: undefined,
            lifecycle: lifecycle(),
            permissions: userPermsContributor(),
            contractor: false
        };
    }

    const contact = (partyId: string, contactId?: string): Contact => {
        return {
            accountId: account.accountId || '',
            partyId: partyId || '',
            contactId: contactId || guid.newGuid(),
            name: '',
            title: '',
            address: address(),
            phone: [] as Phone[],
            email: '',
            lifecycle: lifecycle(),
        }
    }

    const userPermsAdmin = (): UserPermissions => {
        return {
            permType: PermissionType.admin,
            adminPerms: {
                accountAdmin: true,
                createCases: true,
                editCases: true,
                deleteCases: true,
                addEditUsers: true,
                deleteUsers: true,
                editCaseDataTypes: true,
                addEditParties: true,
                deleteParties: true,
                changeAccountSettings: true,
            },
            accountPerms: {
                createEditCaseTemplates: true,
                deleteCaseTemplates: true,
                createEditEngagementTemplates: true,
                deleteEngagementTemplates: true,
            },
            casePerms: {
                caseLead: true,
                editCaseInfo: true,
                editEngagement: true,
                viewEngagement: true,
                createMilestones: true,
                editMilestones: true,
                deleteMilestones: true,
                completeMilestones: true,
                reorderMilestones: true,
                reopenMilestones: true,
                viewAllTasks: true,
                createTasksForOthers: true,
                createTasks: true,
                editTasks: true,
                addEvents: true,
                editEvents: true,
                deleteEvents: true,
                addNotes: true,
                viewDocuments: true,
                editDocuments: true,
                deleteDocuments: true,
                uploadDocuments: true,
                downloadDocuments: true,
                addEditTrackedTime: true,
                deleteTrackedTime: true,
                viewExpenses: true,
                addExpenses: true,
                deleteOwnExpenses: true,
                viewBillableRates: true,
                editBillableRates: true,
                viewInvoices: true,
                createInvoices: true,
                deleteInvoices: true,
            },
        }
    }

    const userPermsCaseLead = (): UserPermissions => {
        return {
            permType: PermissionType.caseLead,
            adminPerms: {
                accountAdmin: false,
                createCases: false,
                editCases: false,
                deleteCases: false,
                addEditUsers: false,
                deleteUsers: false,
                editCaseDataTypes: false,
                addEditParties: false,
                deleteParties: false,
                changeAccountSettings: false,
            },
            accountPerms: {
                createEditCaseTemplates: false,
                deleteCaseTemplates: false,
                createEditEngagementTemplates: false,
                deleteEngagementTemplates: false,
            },
            casePerms: {
                caseLead: true,
                editCaseInfo: true,
                editEngagement: true,
                viewEngagement: true,
                createMilestones: true,
                editMilestones: true,
                deleteMilestones: true,
                completeMilestones: true,
                reorderMilestones: true,
                reopenMilestones: true,
                viewAllTasks: true,
                createTasksForOthers: true,
                createTasks: true,
                editTasks: true,
                addEvents: true,
                editEvents: true,
                deleteEvents: true,
                addNotes: true,
                viewDocuments: true,
                editDocuments: true,
                deleteDocuments: true,
                uploadDocuments: true,
                downloadDocuments: true,
                addEditTrackedTime: true,
                deleteTrackedTime: true,
                viewExpenses: true,
                addExpenses: true,
                deleteOwnExpenses: true,
                viewBillableRates: true,
                editBillableRates: true,
                viewInvoices: true,
                createInvoices: true,
                deleteInvoices: true,
            },
        }
    }

    const userPermsContributor = (): UserPermissions => {
        return {
            permType: PermissionType.contributor,
            adminPerms: {
                accountAdmin: false,
                createCases: false,
                editCases: false,
                deleteCases: false,
                addEditUsers: false,
                deleteUsers: false,
                editCaseDataTypes: false,
                addEditParties: false,
                deleteParties: false,
                changeAccountSettings: false,
            },
            accountPerms: {
                createEditCaseTemplates: false,
                deleteCaseTemplates: false,
                createEditEngagementTemplates: false,
                deleteEngagementTemplates: false,
            },
            casePerms: {
                caseLead: false,
                editCaseInfo: true,
                editEngagement: true,
                viewEngagement: true,
                createMilestones: false,
                editMilestones: false,
                deleteMilestones: false,
                completeMilestones: false,
                reorderMilestones: false,
                reopenMilestones: false,
                viewAllTasks: false,
                createTasksForOthers: false,
                createTasks: true,
                editTasks: true,
                addEvents: true,
                editEvents: true,
                deleteEvents: true,
                addNotes: true,
                viewDocuments: true,
                editDocuments: true,
                deleteDocuments: false,
                uploadDocuments: true,
                downloadDocuments: true,
                addEditTrackedTime: true,
                deleteTrackedTime: true,
                viewExpenses: true,
                addExpenses: true,
                deleteOwnExpenses: true,
                viewBillableRates: false,
                editBillableRates: false,
                viewInvoices: true,
                createInvoices: false,
                deleteInvoices: false,
            },
        }
    }

    const party = (partyId?: string): Party => {
        return {
            accountId: account.accountId || '',
            partyId: partyId || guid.newGuid(),
            name: '',
            address: address(),
            phone: [] as Phone[],
            url: '',
            logoUrl: '',
            lifecycle: lifecycle()
        }
    }

    const partyWithContacts = (partyId?: string): PartyWithContacts => {
        return {
            party: party(partyId),
            contacts: [] as Contact[]
        }
    }

    const caseEvent = (caseId: string, eventId?: string): CaseEvent => {
        return {
            accountId: account.accountId || '',
            caseId: caseId || '',
            eventId: eventId || guid.newGuid(),
            event: '',
            description: '',
            location: '',
            eventType: '',
            date: undefined,
            startDateTime: undefined,
            endDateTime: undefined,
            due: false,
            allDay: false,
            ownerId: '',
            priority: '',
            followUpRequired: false,
            billable: false,
            lifecycle: lifecycle()
        }
    }

    const caseNote = (caseId: string, noteId?: string): Note => {
        return {
            accountId: account.accountId || '',
            caseId: caseId || '',
            noteId: noteId || guid.newGuid(),
            text: '',
            milestoneId: undefined,
            lifecycle: lifecycle()
        }
    }

    const caseDocument = (caseId: string, docId?: string): CaseDocument => {
        return {
            accountId: account.accountId || '',
            caseId: caseId || '',
            docId: docId || guid.newGuid(),
            description: '',
            name: '',
            size: 0,
            summary: '',
            lifecycle: lifecycle()
        }
    }

    const caseObj = (caseId?: string): Case => {

        return {
            accountId: account.accountId || '',
            caseId: caseId || guid.newGuid(),
            caseName: '',
            caseType: '',
            shortDescription: {
                fieldName: 'Description / Action / Proceeding',
                fieldValue: '',
            },
            retainedBy: partyWithContacts(),
            retainedDate: undefined,
            overview: '',
            isFixedFee: false,
            fixedAmount: '0',
            estimatedHours: '0',
            affiliatedParties: [] as PartyWithContacts[],
            assignedUsers: [] as UserRef[],
            status: undefined,
            statusChanges: [] as CaseStatusChange[],
            allowedStatuses: [] as CaseStatus[],
            lifecycle: lifecycle(),
        };
    }

    const caseStatus = (sysStatus?: CaseSystemStatus): CaseStatus => {
        return {
            status: '',
            systemStatus: sysStatus || CaseSystemStatus.new,
            description: '',
            order: 0,
        }
    }

    const caseObjWithNotesEvents = (caseId?: string): Case => {

        return {
            accountId: account.accountId || '',
            caseId: caseId || guid.newGuid(),
            caseName: '',
            caseType: '',
            shortDescription: {
                fieldName: 'Description / Action / Proceeding',
                fieldValue: ''
            },
            retainedBy: partyWithContacts(),
            retainedDate: undefined,
            overview: '',
            isFixedFee: false,
            fixedAmount: '0',
            affiliatedParties: [] as PartyWithContacts[],
            status: undefined,
            allowedStatuses: [] as CaseStatus[],
            caseNotes: [] as Note[],
            caseEvents: [] as CaseEvent[],
            lifecycle: lifecycle(),
        }
    }

    const task = (userId: string, caseId?: string, taskId?: string): Task => {
        return {
            accountId: account.accountId || '',
            caseId: caseId || '',
            userId: userId,
            taskId: taskId || guid.newGuid(),
            milestoneId: undefined,
            title: '',
            text: '',
            status: TaskStatus.open,
            billable: false,
            dueBy: undefined,
            hasNotes: false,
            category: '',
            estimatedHours: '0',
            results: '',
            lifecycle: lifecycle()
        }
    }

    const caseDocumentReference = (caseId?: string, docId?: string): DocumentReference => {
        return {
            caseId: caseId || '',
            documentId: docId || '',
        }
    }

    const timeTrackEntry = (caseId: string, userId: string, entryId?: string, date?: Date): TimeTrackingEntry => {
        return {
            hours: 0,
            description: '',
            accountId: account.accountId || '',
            caseId: caseId || '',
            userId: userId || '',
            date: date || new Date(),
            entryId: entryId || guid.newGuid(),
            invoiceId: '',
            lifecycle: lifecycle()
        }
    }

    const caseExpense = (caseId: string, username: string, expenseId?: string): CaseExpense => {
        return {
            accountId: account?.accountId || '',
            caseId: caseId || '',
            expenseId: expenseId || guid.newGuid(),
            username: username,
            text: '',
            date: new Date(),
            amount: '',
            type: '',
            attachmentId: '',
            attachmentName: '',
            invoiceId: '',
            recoverable: true,
        }
    }

    const caseEngagement = (caseId: string, engagementDocId?: string): CaseEngagementDoc => {
        return {
            accountId: account?.accountId || '',
            caseId: caseId || '',
            engagementDocId: engagementDocId || guid.newGuid(),
            isAddendum: false,
            title: '',
            paymentBy: '',
            addSignatures: false,
            fields: [] as FieldEntry[],
            accountSignatures: [] as UserRef[],
            clientSignatures: [] as Contact[],
            lifecycle: lifecycle(),
            isExecuted: false,
        }
    }

    const engagementTemplate = (templateId?: string): EngagementTemplate => {
        return {
            accountId: account?.accountId || '',
            templateId: templateId || guid.newGuid(),
            templateName: '',
            title:'',
            description: '',
            addSignatures: false,
            fields: [] as FieldEntry[],
            lifecycle: lifecycle(),
        }
    }

    const caseTemplate = (templateId?: string): CaseTemplate => {
        return {
            accountId: account?.accountId || '',
            templateId: templateId || guid.newGuid(),
            templateName: '',
            templateDescription: '',
            shortDescription: { fieldName: 'Description / Action / Proceeding', fieldValue: '' } as FieldEntry,
            events: [] as CaseEventTemplate[],
            milestones: [] as CaseMilestoneTemplate[],
            notes: [] as string[],
            caseType: undefined,
            overview: '',
            engagementTemplate: undefined,
            statuses: [] as CaseStatus[],
        }
    }

    const fileUploadInfo = (caseId: string, fileId: string, file: File, otherUpload: boolean = false): FileUploadInfo => {
        return {
            caseId,
            fileId,
            file: file,
            fileName: file.name,
            fileSize: file.size,
            progress: 0,
            uploadTime: 0,
            uploadStarted: false,
            uploadComplete: false,
            otherUpload
        }
    }

    const caseMilestone = (caseId?: string): CaseMilestone => {
        return {
            accountId: account?.accountId || '',
            caseId: caseId || '',
            milestoneId: guid.newGuid(),
            name: '',
            description: '',
            completed: false,
            completedBy: undefined,
            completedOn: undefined,
            deliverables: '',
            deliverablesDelivered: false,
            dueDate: undefined,
            sortKey: 0,
            lifecycle: lifecycle(),

            escalationDate: undefined,
            category: '',
            priority: 3,
            responsibleUser: '',
            secondaryReviewer: '',
            externalDependencies: '',
            blocked: false,
            currentBlockers: '',
            blockerOwner: '',
            definitionOfDone: '',
            approvalStatus: '',
            approvedBy: '',
            resultSummary: '',
            linkedDocuments: [] as string[],
            externalLinks: [] as string[],
            estimatedHoursCost: '',
            actualHoursCost: '',
            budgetVarianceReason: '',
            lessonsLearned: '',
        }
    }

    const caseMilestoneTemplate = (): CaseMilestoneTemplate => {
        return {
            id: guid.newGuid(),
            name: 'New Milestone',
            description: '',
            deliverables: '',
            dueDaysFromCreated: 0,
            events: [] as CaseEventTemplate[],
            tasks: [] as TaskTemplate[],
        }
    }

    const taskTemplate = (): TaskTemplate => {
        return {
            id: guid.newGuid(),
            billable: true,
            title: '',
            text: '',
            username: '',
            userId: ''
        }
    }

    const caseEventTemplate = (): CaseEventTemplate => {
        return {
            id: guid.newGuid(),
            eventName: '',
            description: '',
            daysFromRetained: 0,
            due: false,
        }
    }

    const featureSet = (): FeatureSet => {
        return {
            seats: 2,
            clientsAndContacts: true,
            engagement: true,
            tasks: true,
            events: true,
            notes: true,
            documents: true,
            documentLimit: 200,
            timeTracking: false,
            invoices: false
        }
    }

    const accountSettings = (): AccountSettings => {
        return {
            billingTimePercentDisplay: metricDisplay(),
            timeLeakagePercentDisplay: metricDisplay(),
            groupStatuses: false,
            mustUseTemplateToCreateEngagement: false,
            timePeriodsForInsights: [] as string[],
            requireTasksBeTiedToMilestones: false,
            cannotCompleteMilestoneBeforePrevious: false,
            mergeInEngagementDocTemplateOnApply: true,
        }
    }

    const metricDisplay = (warning?: number, danger?: number): MetricDisplay => {
        return {
            use: true,
            warning: warning ?? 55,
            danger: danger ?? 35,
        }
    }

    const invoice = (caseId?: string, fixedAmount?: string): Invoice => {
        return {
            accountId: account?.accountId || '',
            caseId: caseId || '',
            invoiceId: guid.newGuid(),
            invoiceNumber: '',
            invoiceDate: new Date(),
            billingPeriodStart: undefined,
            billingPeriodEnd: undefined,
            dueDate: new Date(),
            fixedAmount: fixedAmount,
            attachEngagement: false,
            paymentBy: PayTerms.uponReceipt,
        };
    }

    return {
        accountSettings,
        address,
        caseEvent,
        caseEventTemplate,
        caseNote,
        caseDocument,
        caseDocumentReference,
        caseEngagement,
        caseExpense,
        caseMilestone,
        caseMilestoneTemplate,
        caseObj,
        caseObjWithNotesEvents,
        caseStatus,
        caseTemplate,
        contact,
        individual,
        invoice,
        featureSet,
        fileUploadInfo,
        lifecycle,
        metricDisplay,
        party,
        partyWithContacts,
        phone,
        engagementTemplate,
        task,
        taskTemplate,
        user,
        userPermsAdmin,
        userPermsCaseLead,
        userPermsContributor,
        timeTrackEntry,
    };
}